package uz.mint.frontend.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.api.Plan
import uz.mint.frontend.api.flag
import uz.mint.frontend.api.priceLabel
import uz.mint.frontend.api.tag
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.hairlineCell
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.toSitePalette

/**
 * The plan card grid, shared by the landing preview and the full listing.
 *
 * Extracted so the two cannot drift: a customer who taps a card on the landing page and the same
 * card on `/plans/` must get the same thing.
 *
 * @param dimmed a newer page is loading and these are the previous results. Fading them beats
 *   replacing the grid with a spinner, which makes every page turn look like a reload.
 */
@Composable
fun PlanGrid(
    plans: List<Plan>,
    locale: Locale,
    strings: SiteStrings,
    dimmed: Boolean = false,
    onSelect: (Plan) -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .fillMaxWidth()
            // Filled like a card: a page of 24 plans rarely divides evenly by the column
            // count, and this is the colour the missing cells of the last row show.
            .backgroundColor(palette.card)
            .padding(2.px)
            .display(DisplayStyle.Grid)
            .gap(2.px)
            .gridTemplateColumnsCss("repeat(auto-fit,minmax(min(100%,262px),1fr))")
            .opacity(if (dimmed) 0.55 else 1.0)
            .transition(Transition.of("opacity", 160.ms))
            .toAttrs()
    ) {
        plans.forEach { PlanCard(it, locale, strings, onSelect) }
    }
}

/**
 * One tariff card.
 *
 * A real `<button>`, not a link: it opens the checkout dialog on this page rather than
 * navigating, so an anchor would be announced as a link to nowhere and would push a dead history
 * entry. (It used to be an `<a>` straight to Telegram, back when there was nothing in between.)
 */
@Composable
private fun PlanCard(plan: Plan, locale: Locale, strings: SiteStrings, onSelect: (Plan) -> Unit) {
    val palette = ColorMode.current.toSitePalette()

    Button(
        attrs = Modifier
            .fillMaxWidth()
            .backgroundColor(palette.card)
            .hairlineCell(palette.rule)
            .color(palette.ink)
            .border(0.px, LineStyle.None, palette.rule)
            .textAlign(TextAlign.Start)
            .padding(top = 22.px, leftRight = 22.px, bottom = 20.px)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(18.px)
            .minHeight(186.px)
            .cursor(Cursor.Pointer)
            .fontFamily("inherit")
            .onClick { onSelect(plan) }
            .toAttrs()
    ) {
        Row(Modifier.fillMaxWidth().gap(10.px), verticalAlignment = Alignment.CenterVertically) {
            SpanText(plan.flag(), Modifier.fontSize(30.px).lineHeight(1))
            Spacer()
            SpanText(
                plan.tag(locale, strings),
                EyebrowStyle.toModifier().fontSize(11.px).letterSpacing(0.1.em).color(palette.accentText),
            )
        }

        Column(Modifier.fillMaxWidth().gap(4.px).styleModifier { property("margin-top", "auto") }) {
            SpanText(
                plan.name,
                Modifier.fontSize(17.px).fontWeight(FontWeight.Bold)
                    .letterSpacing((-0.015).em).lineHeight(1.2),
            )
            plan.region?.let {
                SpanText(it, Modifier.fontSize(13.px).color(palette.ink2).opacity(0.8))
            }
        }

        Row(
            Modifier.fillMaxWidth().gap(10.px)
                .borderTop(2.px, LineStyle.Solid, palette.rule).padding(top = 12.px),
            verticalAlignment = Alignment.Bottom,
        ) {
            Column(Modifier.minWidth(0.px)) {
                SpanText(
                    strings.tariffsFrom,
                    EyebrowStyle.toModifier().fontSize(11.px).letterSpacing(0.1.em).opacity(0.55),
                )
                // Smaller than the design's 26px: "1 265 000 сум" is three times the width of
                // "$12.99", and the price has to stay on one line beside the arrow.
                SpanText(
                    plan.priceLabel(strings),
                    Modifier.fontSize(22.px).fontWeight(FontWeight.ExtraBold)
                        .letterSpacing((-0.03).em).lineHeight(1.1),
                )
            }
            Spacer()
            ArrowRightIcon(22, palette.ink)
        }
    }
}

/** Centred one-line message for the loading, empty and no-results states. */
@Composable
fun PlanNotice(text: String) {
    val palette = ColorMode.current.toSitePalette()
    Div(
        Modifier.fillMaxWidth().padding(topBottom = 48.px).textAlign(TextAlign.Center)
            .lineHeight(1.6).color(palette.ink2).toAttrs()
    ) { SpanText(text) }
}

/** Failed-fetch state with a retry, used wherever a plan list can fail to load. */
@Composable
fun PlanError(strings: SiteStrings, onRetry: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Column(
        Modifier.fillMaxWidth().padding(topBottom = 48.px).gap(18.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(strings.tariffsError, Modifier.color(palette.ink2))
        SecondaryButton(strings.tariffsRetry, onRetry)
    }
}
